import {detectBaseUrl} from "../utils/utils";

const createAudioProcessor = (WebSocketService) => {
    let audioContext = null;
    let processor = null;
    let input = null;
    let stream = null;
    let isRecording = false;
    let workletLoaded = false;
    let isProcessorInitialized = false;
    const baseUrl = detectBaseUrl();

    const startAudioProcessing = async () => {
        if (isRecording) {
            console.warn("Websocket audio processing is already running.");
            return false;
        }

        try {
            if (isProcessorInitialized && processor && input && audioContext) {
                isRecording = true;
                return true;
            }

            stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    noiseSuppression: true,
                    echoCancellation: true,
                    autoGainControl: true,
                    channelCount: 1,
                    sampleRate: 16000
                }
            });

            if (!audioContext) {
                audioContext = new AudioContext({sampleRate: 16000});
            }

            if (!workletLoaded) {
                try {
                    try {
                        await audioContext.audioWorklet.addModule(`${baseUrl}/VoiceDetectionProcessor.js`);
                        workletLoaded = true;
                    } catch (e) {
                        console.warn("Failed to load AudioWorklet module from baseUrl, trying relative path:", e);

                        try {
                            workletLoaded = true;
                        } catch (e2) {
                            console.warn("Failed to load AudioWorklet module from relative path, trying public directory:", e2);

                            try {
                                await audioContext.audioWorklet.addModule(`${window.location.origin}/public/VoiceDetectionProcessor.js`);
                                workletLoaded = true;
                            } catch (e3) {
                                console.warn("Failed to load AudioWorklet module from public directory, trying build directory:", e3);

                                await audioContext.audioWorklet.addModule(`${window.location.origin}/build/VoiceDetectionProcessor.js`);
                                workletLoaded = true;
                            }
                        }
                    }
                } catch (e) {
                    console.error("Failed to load AudioWorklet module from all paths:", e);
                    return false;
                }
            }

            if (!input) {
                input = audioContext.createMediaStreamSource(stream);
            }

            if (!processor) {
                processor = new AudioWorkletNode(audioContext, 'voice-detection-processor');

                processor.port.onmessage = (event) => {
                    if (!isRecording) return;

                    const {type, data} = event.data;

                    if (type === 'audioData') {
                        WebSocketService.sendData(data);
                    }
                };

                input.connect(processor);
                processor.connect(audioContext.destination);

                isProcessorInitialized = true;
            }

            isRecording = true;
            return true;
        } catch (error) {
            console.error("Error starting Websocket audio processing:", error);
            return false;
        }
    };

    const stopAudioProcessing = (fullCleanup = false) => {
        isRecording = false;

        if (processor) {
            if (processor instanceof AudioWorkletNode) {
                processor.port.postMessage({type: 'reset'});

                if (fullCleanup) {
                    processor.port.onmessage = null;
                }
            }

            processor.disconnect();
            if (fullCleanup) {
                processor = null;
                isProcessorInitialized = false;
            }
        }

        if (input) {
            input.disconnect();
            if (fullCleanup) {
                input = null;
            }
        }

        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            if (fullCleanup) {
                stream = null;
            }
        }

        if (audioContext && fullCleanup) {
            audioContext.close().catch(err => console.error("Error closing audio context:", err));
            audioContext = null;
            workletLoaded = false;
        }
    };

    return {
        startAudioProcessing,
        stopAudioProcessing,
        isRecording: () => isRecording
    };
};

export default createAudioProcessor;
